<template>
  <div class="certificate-page">
    <div class="certificate-card">
      <div class="card-header">
        <h2 class="card-title" @click="handlelink">证书</h2>
      </div>
      <div v-if="hasCertError" class="certificate-info-card-text">
        <div>{{ certErrorMessage }}</div>
        <van-button
          type="primary"
          size="small"
          class="refresh-btn"
          :loading="refreshing"
          @click="handleRefresh"
        >
          <van-icon name="replay" /> 刷新
        </van-button>
      </div>
      <div v-else-if="(isShowCert && !hasCertImage) || isCertificateIssuing">
        <div class="certificate-info-card-text">
          <div>证书颁发中...</div>
          <van-button
            type="primary"
            size="small"
            class="refresh-btn"
            :loading="refreshing"
            @click="handleRefresh"
          >
            <van-icon name="replay" /> 刷新
          </van-button>
        </div>
      </div>
      <!-- ✅ 新增：证书数据加载中 -->
      <div v-else-if="certLoading" class="certificate-loading-container">
        <van-loading type="spinner" vertical>证书加载中...</van-loading>
        <van-button
          type="primary"
          size="small"
          class="refresh-btn"
          :loading="refreshing"
          @click="handleRefresh"
          style="margin-top: 16px"
        >
          <van-icon name="replay" /> 刷新
        </van-button>
      </div>
      <div
        class="certificate-container"
        v-else-if="detailInfo.cert && detailInfo.cert.length > 0"
      >
        <div
          class="certificate-image"
          v-for="(item, index) in detailInfo.cert"
          :key="index"
        >
          <!-- 证书信息不完全 -->
          <div v-if="item.errorMessage" class="certificate-placeholder">
            <div class="placeholder-content">
              <p>{{ item.errorMessage }}</p>
            </div>
          </div>
          <template v-else>
            <!-- ✅ 新增：图片加载状态 -->
            <div
              v-if="imageLoadingMap[index]"
              class="certificate-image-loading"
            >
              <van-loading type="spinner" vertical>图片加载中...</van-loading>
            </div>
            <img
              v-if="item.imageUrl && !imageErrorMap[index]"
              :src="getCertImageUrl(item, index)"
              class="certificate-img"
              :class="{ 'img-hidden': imageLoadingMap[index] }"
              @load="handleImageLoad(index)"
              @error="handleImageError(index)"
            />
            <div v-else class="certificate-placeholder">
              <div class="placeholder-content">
                <p>证书颁发中...</p>
                <van-button
                  type="primary"
                  size="small"
                  class="refresh-btn"
                  :loading="refreshing"
                  @click="handleRefresh"
                >
                  <van-icon name="replay" /> 刷新
                </van-button>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div v-else>
        <div class="certificate-info-card-text">
          <div>
            {{
              detailInfo.hasPendingQuestionnaire
                ? "请填写完问卷才能查看成绩或证书"
                : "考试已完成"
            }}
          </div>
          <!-- 如果已通过考试但还没有证书，显示刷新按钮 -->
          <van-button
            v-if="isCertificateIssuing"
            type="primary"
            size="small"
            class="refresh-btn"
            :loading="refreshing"
            @click="handleRefresh"
          >
            <van-icon name="replay" /> 刷新
          </van-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from "vue-router";
import { getFileFullUrl } from "@/utils";
import {
  computed,
  nextTick,
  ref,
  onBeforeUnmount,
  onMounted,
  watch,
} from "vue";
import { Toast } from "vant";

const router = useRouter();
const isShowCert = ref(false);
// ✅ 新增：证书数据加载状态（当 cert 为 undefined 时显示 loading）
const certLoading = ref(false);
// ✅ 新增：图片加载状态映射（key: 证书索引, value: 是否正在加载）
const imageLoadingMap = ref({});
const imageErrorMap = ref({});
const imageRetryMap = ref({});
const imageAutoRefreshTimers = {};
const imageAutoRefreshAttempts = {};
const IMAGE_AUTO_REFRESH_DELAY = 3000;
const MAX_IMAGE_AUTO_REFRESH_ATTEMPTS = 5;
// ✅ 新增：超时定时器，用于避免一直显示 loading
let loadingTimeout = null;
// ✅ 新增：刷新状态
const refreshing = ref(false);

const props = defineProps({
  detailInfo: {
    type: Object,
    default: () => ({}),
  },
  refreshData: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(["refresh"]);

const certList = computed(() => {
  return Array.isArray(props.detailInfo?.cert) ? props.detailInfo.cert : [];
});

const certError = computed(() => {
  return certList.value.find((item) => item?.errorMessage);
});

const certErrorMessage = computed(() => {
  return certError.value?.errorMessage || "";
});

const hasCertError = computed(() => {
  return Boolean(certErrorMessage.value);
});

const hasCertImage = computed(() => {
  return certList.value.some((item) => item?.imageUrl);
});

const getCertImageUrl = (item, index) => {
  const url = getFileFullUrl(item.imageUrl);
  const retry = imageRetryMap.value[index] || 0;
  if (!retry) {
    return url;
  }
  return `${url}${url.includes("?") ? "&" : "?"}_certRetry=${retry}`;
};

const isCertificateIssuing = computed(() => {
  return (
    props.detailInfo?.step === "PASSED" &&
    props.detailInfo?.hasPendingQuestionnaire !== true &&
    certList.value.length === 0
  );
});

// ✅ 新增：判断是否应该显示证书数据 loading
const shouldShowCertLoading = () => {
  // 如果 detailInfo 不存在，不显示 loading
  if (!props.detailInfo) {
    return false;
  }

  // 如果 cert 是 undefined，说明数据正在加载
  if (props.detailInfo.cert === undefined) {
    return true;
  }

  // 如果 cert 是空数组，说明数据已加载完成，只是没有证书，不显示 loading
  return false;
};

// ✅ 新增：监听 detailInfo.cert 的变化，判断是否正在加载
watch(
  () => props.detailInfo?.cert,
  (newCert) => {
    // 清除之前的超时定时器
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
      loadingTimeout = null;
    }

    // 如果 cert 从 undefined 变为有值（包括空数组），说明加载完成
    if (newCert !== undefined) {
      certLoading.value = false;

      // 如果 cert 有数据，初始化图片加载状态
      if (Array.isArray(newCert) && newCert.length > 0) {
        newCert.forEach((item, index) => {
          if (item?.imageUrl) {
            imageLoadingMap.value[index] = true;
            imageErrorMap.value[index] = false;
            imageRetryMap.value[index] = (imageRetryMap.value[index] || 0) + 1;
          }
        });
      }
    } else {
      // 如果 cert 是 undefined，且 detailInfo 已存在，显示 loading
      if (props.detailInfo) {
        certLoading.value = true;
        // 设置超时，避免一直显示 loading（例如网络错误导致数据永远加载不完）
        loadingTimeout = setTimeout(() => {
          certLoading.value = false;
        }, 10000); // 10秒后停止 loading
      }
    }
  },
  { immediate: true }
);

// ✅ 新增：图片加载成功处理
const handleImageLoad = (index) => {
  imageLoadingMap.value[index] = false;
  imageErrorMap.value[index] = false;
  imageAutoRefreshAttempts[index] = 0;
  clearImageAutoRefreshTimer(index);
};

// ✅ 新增：图片加载失败处理
const handleImageError = (index) => {
  imageLoadingMap.value[index] = false;
  imageErrorMap.value[index] = true;
  scheduleImageAutoRefresh(index);
};

const clearImageAutoRefreshTimer = (index) => {
  if (imageAutoRefreshTimers[index]) {
    clearTimeout(imageAutoRefreshTimers[index]);
    delete imageAutoRefreshTimers[index];
  }
};

const scheduleImageAutoRefresh = (index) => {
  if (imageAutoRefreshTimers[index]) {
    return;
  }

  const attempts = imageAutoRefreshAttempts[index] || 0;
  if (attempts >= MAX_IMAGE_AUTO_REFRESH_ATTEMPTS) {
    return;
  }

  imageAutoRefreshTimers[index] = setTimeout(async () => {
    delete imageAutoRefreshTimers[index];
    imageAutoRefreshAttempts[index] = attempts + 1;

    try {
      if (props.refreshData) {
        await props.refreshData();
        await nextTick();
      } else {
        emit("refresh");
      }
    } finally {
      if (imageErrorMap.value[index]) {
        imageRetryMap.value[index] = (imageRetryMap.value[index] || 0) + 1;
        imageErrorMap.value[index] = false;
        imageLoadingMap.value[index] = true;
      }
    }
  }, IMAGE_AUTO_REFRESH_DELAY);
};

const handlelink = () => {
  router.push({
    path: "/mobileHomev1",
  });
};

// ✅ 新增：处理刷新按钮点击
const handleRefresh = async () => {
  if (refreshing.value) {
    return; // 防止重复点击
  }

  try {
    refreshing.value = true;
    if (props.refreshData) {
      const res = await props.refreshData();
      await nextTick();

      if (res?.success === false) {
        Toast.fail(res.message || "刷新失败，请稍后重试");
      } else if (hasCertError.value) {
        Toast.fail(certErrorMessage.value);
      } else if (hasCertImage.value) {
        Toast.success("证书已生成");
      } else if (isCertificateIssuing.value) {
        Toast("证书仍在生成中，请稍后刷新");
      } else {
        Toast.success("刷新成功");
      }
    } else {
      // 触发父组件的刷新方法
      emit("refresh");
    }
  } catch (error) {
    console.error("刷新失败:", error);
    Toast.fail("刷新失败，请稍后重试");
  } finally {
    refreshing.value = false;
  }
};

onMounted(() => {
  const isShowCertificate = localStorage.getItem("isShowCertificate");
  // 从考试页返回回来 证书显示正在审核中
  if (isShowCertificate) {
    localStorage.removeItem("isShowCertificate");
    isShowCert.value = isShowCertificate && props.detailInfo.step == "PASSED";
  }

  // ✅ 新增：初始化时检查证书数据状态
  if (shouldShowCertLoading()) {
    certLoading.value = true;
    // 设置超时，避免一直显示 loading
    loadingTimeout = setTimeout(() => {
      certLoading.value = false;
    }, 10000);
  }
});

onBeforeUnmount(() => {
  if (loadingTimeout) {
    clearTimeout(loadingTimeout);
    loadingTimeout = null;
  }
  Object.keys(imageAutoRefreshTimers).forEach((index) => {
    clearImageAutoRefreshTimer(index);
  });
});
</script>

<style scoped lang="less">
.certificate-card {
  background: #ffffff;
  padding: 20px 16px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;

  .card-header {
    margin-bottom: 20px;
    border-bottom: 1px solid #f3f4f6;
    padding-bottom: 16px;

    .card-title {
      font-size: 18px;
      font-weight: 600;
      color: #1f2937;
      margin: 0;
      text-align: left;
    }
  }
}
.certificate-info-card-text {
  text-align: center;
  padding: 60px 0;
  font-size: 16px;
  color: #666666;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .refresh-btn {
    margin-top: 8px;
    min-width: 100px;
  }
}

// ✅ 新增：证书数据加载容器样式
.certificate-loading-container {
  padding: 60px 0;
  text-align: center;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.certificate-container {
  .certificate-image {
    margin-bottom: 20px;
    text-align: center;
    min-height: 200px;
    position: relative;

    // ✅ 新增：图片加载容器样式
    .certificate-image-loading {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 2;
      padding: 20px;
      background: rgba(255, 255, 255, 0.9);
      border-radius: 8px;
    }

    .certificate-img {
      width: 100%;
      max-width: 600px;
      height: auto;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      border: 1px solid #e5e7eb;
      transition: opacity 0.3s ease;

      // ✅ 新增：图片加载中时隐藏
      &.img-hidden {
        opacity: 0;
        visibility: hidden;
      }
    }

    .certificate-placeholder {
      width: 100%;
      height: 200px;
      background: #f9fafb;
      border: 2px dashed #d1d5db;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;

      .placeholder-content {
        text-align: center;
        color: #6b7280;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;

        p {
          font-size: 16px;
          margin: 0;
        }

        .refresh-btn {
          margin-top: 8px;
          min-width: 100px;
        }
      }
    }
  }
}
</style>
